import {
  useCallback,
  ReactNode,
  useState
} from 'react';
import PageContext, {
  IPageContext,
  SubNavigation,
  UndoActionState
} from './context';

export interface PageProviderProps {
  children: ReactNode;
}

const isSameUndoAction = (left: UndoActionState | null, right: UndoActionState | null) => (
  left?.enabled === right?.enabled
  && left?.loading === right?.loading
  && left?.onClick === right?.onClick
);

export default function PageProvider({ children }: PageProviderProps) {
  const [title, setTitle] = useState<string | null>(null);
  const [parentPath, setParentPath] = useState<string | null>(null);
  const [parentTitle, setParentTitle] = useState<string | null>(null);
  const [subNavigation, setSubNavigation] = useState<SubNavigation[]>([]);
  const [topBarActions, setTopBarActions] = useState<ReactNode[]>([]);
  const [undoAction, setUndoAction] = useState<UndoActionState>({
    enabled: false,
    loading: false,
    onClick: null,
  });
  const [undoActionOverride, setUndoActionOverride] = useState<UndoActionState | null>(null);
  const setUndoActionSafe = useCallback((nextState: UndoActionState | ((prev: UndoActionState) => UndoActionState)) => {
    setUndoAction((previousState) => {
      const resolved = typeof nextState === 'function'
        ? (nextState as (prev: UndoActionState) => UndoActionState)(previousState)
        : nextState;
      return isSameUndoAction(previousState, resolved) ? previousState : resolved;
    });
  }, []);
  const setUndoActionOverrideSafe = useCallback((nextState: UndoActionState | null | ((prev: UndoActionState | null) => UndoActionState | null)) => {
    setUndoActionOverride((previousState) => {
      const resolved = typeof nextState === 'function'
        ? (nextState as (prev: UndoActionState | null) => UndoActionState | null)(previousState)
        : nextState;
      return isSameUndoAction(previousState, resolved) ? previousState : resolved;
    });
  }, []);
  const clearUndoActionOverride = useCallback(() => {
    setUndoActionOverrideSafe(null);
  }, [setUndoActionOverrideSafe]);

  const effectiveUndoAction = undoActionOverride ?? undoAction;

  const value: IPageContext = {
    state: {
      title,
      parentPath,
      parentTitle,
      subNavigation,
      topBarActions,
      undoAction: effectiveUndoAction,
    },
    actions: {
      setTitle,
      setParentPath,
      setParentTitle,
      setSubNavigation,
      setTopBarActions,
      setUndoAction: setUndoActionSafe as typeof setUndoAction,
      setUndoActionOverride: setUndoActionOverrideSafe as typeof setUndoActionOverride,
      clearUndoActionOverride,
    },
  };

  return (
    <PageContext.Provider value={value}>
      {children}
    </PageContext.Provider>
  );
}
